import type { ChartDataset, ScatterDataPoint } from 'chart.js';
import trim from 'lodash/trim';
import React, { CSSProperties, FC } from 'react';
import { PlcColor } from '@styles/colors';
import { formatNumberWithComma } from '@utils/number';
import styles from './ChartTooltip.module.scss';

export type ChartTooltipColors = {
  [index: number]: CSSProperties['backgroundColor'];
};

interface Props {
  title: string;
  index: number;
  datasets: ChartDataset<'line', (number | ScatterDataPoint)[]>[];
  unit?: string;
  colors?: ChartTooltipColors;
}

export const ChartTooltip: FC<Props> = ({ title, index, datasets, unit = '', colors = {} }) => {
  const datasetsToDisplay = datasets.filter((ds) => Array.isArray(ds.data) && typeof ds.data[index] !== 'undefined');

  return (
    <div className={styles.chartTooltip}>
      <p className={styles.textTitleTooltip}>{title}</p>
      <table>
        <tbody>
          {datasetsToDisplay.map((dataset, datasetIndex) => {
            const raw = dataset.data?.[index] as number | ScatterDataPoint | undefined;
            const value = typeof raw === 'number' ? raw : raw?.y;

            // Need the typeof check to avoid filtering 0
            if (typeof value === 'undefined') {
              return null;
            }

            // borderColor may be string, string[], CanvasGradient, CanvasPattern ...
            const border = dataset.borderColor as unknown;
            const borderColor =
              typeof border === 'string'
                ? border
                : Array.isArray(border) && typeof border[0] === 'string'
                  ? border[0]
                  : PlcColor.BLUE_LAND;
            const color = colors[datasetIndex] ?? borderColor;

            const formatted = formatNumberWithComma(value, 1);

            return (
              <tr key={dataset.label ?? `ds-${datasetIndex}`}>
                <th>
                  <span className={styles.textTooltip} style={{ color }}>
                    {dataset.label}
                  </span>
                </th>
                <td align="right">
                  <span className={styles.textTooltip} style={{ color }}>
                    {trim(`${formatted} ${unit}`)}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
